import humps from "humps";
import axios from "axios";
// import config from "src/utils/config";
import * as ls from "local-storage";
import { localStorageKeys } from "src/utils/constant";
import queryString from "query-string";
import { safeDecodeURIComponent } from "src/utils/common";
import store from "src/stores";

export class ApiManager {
  baseUrl: string;
  apiVersion: string;
  referer: string;
  cookie: any;
  constructor(baseUrl?: string, apiVersion?: string, referer?: string) {
    this.baseUrl = baseUrl || process.env.REACT_APP_API_BASE_URL;
    this.apiVersion = apiVersion || process.env.REACT_APP_API_VERSION;
    this.referer = referer;
  }

  getUrl(url: string) {
    if (url.indexOf("?") > -1) {
      const endpoints = url.split("?");
      const qs = safeDecodeURIComponent(endpoints[1] as string);
      const data = queryString.parse(qs, {
        decode: false,
      });

      Object.keys(data).forEach((key) => {
        const val = data[key];
        if (typeof val === "string") {
          data[key] = val.trim();
        }
      });

      return this.apiVersion
        ? `${this.baseUrl}/${this.apiVersion}${
            endpoints[0]
          }?${queryString.stringify(data)}`
        : `${this.baseUrl}${endpoints[0]}?${queryString.stringify(data)}`;
    }
    return this.apiVersion
      ? `${this.baseUrl}/${this.apiVersion}${url}`
      : `${this.baseUrl}/${url}`;
  }

  getHeader(isBearerPrefix = true, authorization = true) {
    const authJs = store.auth.getAuthenticationJs();
    const token = (authJs?.jwtAuthToken ||
      ls.get(localStorageKeys.KEY_TOKEN)) as string | undefined;
    const authorizationHeader =
      authorization && token
        ? `${isBearerPrefix === false ? "" : "Bearer "}${token}`
        : undefined;

    const headers = {
      Authorization: authorizationHeader,
      "Domain-Auth": authJs?.domainAuth,
      "User-Language": authJs?.lang,
      "user-channel": authJs?.userChannel,
      appversion: authJs?.appVersion,
      "Channel-Type": authJs?.channelType,
      Provider: authJs?.provider,
      Category: authJs?.category,
      ClientId: authJs?.clientId,
    };

    return Object.fromEntries(
      Object.entries(headers).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      ),
    ) as any;
  }

  handleErrorStatus(error) {
    if (error && error.response) {
      const { response } = error;

      if (response.status === 401) {
        ls.remove(localStorageKeys.KEY_TOKEN);
        ls.remove(localStorageKeys.KEY_USER);
        ls.remove(localStorageKeys.KEY_PERMISSION);
        window.location.reload();
      }
    }
  }

  async auth(url: string, request: object) {
    const result = await axios({
      url: this.getUrl(url),
      method: "post",
      data: request,
    });

    if (result && result.status === 200) {
      return humps.camelizeKeys(result.data);
    }

    return undefined;
  }

  async get(
    url: string,
    params?: any,
    isBearerPrefix = true,
    authorization = true,
  ) {
    try {
      const result = await axios({
        url: this.getUrl(url),
        method: "get",
        params,
        headers: this.getHeader(isBearerPrefix, authorization),
      });

      if (result && result.status === 200) {
        return result.data;
      }

      return undefined;
    } catch (error) {
      this.handleErrorStatus(error);
      throw error;
    }
  }

  async download(url: string) {
    try {
      const result = await axios({
        url: this.getUrl(url),
        method: "get",
        responseType: "blob",
        headers: this.getHeader(),
      });

      if (result && result.status === 200) {
        return result;
      }

      return undefined;
    } catch (error) {
      this.handleErrorStatus(error);
      throw error;
    }
  }

  async post(
    url: string,
    data?: any,
    isFormData?: boolean,
    isBearerPrefix = true,
    authorization = true,
  ) {
    try {
      const bodyFormData = new FormData();
      if (isFormData && data) {
        for (const d in data) {
          if (Array.isArray(data[d])) {
            for (const a of data[d]) {
              bodyFormData.append(d, a);
            }
          } else {
            bodyFormData.append(d, data[d]);
          }
        }
      }
      const result = await axios({
        url: this.getUrl(url),
        method: "post",
        data: isFormData ? bodyFormData : data,
        headers: isFormData
          ? {
              "Content-Type": "multipart/form-data",
              ...this.getHeader(isBearerPrefix, authorization),
            }
          : this.getHeader(isBearerPrefix, authorization),
      });

      if (result && result.status === 200) {
        return result.data;
      }

      return undefined;
    } catch (error) {
      this.handleErrorStatus(error);
      throw error;
    }
  }

  async put(
    url: string,
    data?: any,
    isFormData?: boolean,
    isBearerPrefix = true,
    authorization = true,
  ) {
    try {
      const result = await axios({
        url: this.getUrl(url),
        method: "put",
        data: isFormData ? data : data,
        headers: isFormData
          ? {
              "Content-Type": "multipart/form-data",
              ...this.getHeader(isBearerPrefix, authorization),
            }
          : this.getHeader(isBearerPrefix, authorization),
      });
      if (result && result.status === 200) {
        return result.data;
      }

      return undefined;
    } catch (error) {
      this.handleErrorStatus(error);
      throw error;
    }
  }

  async delete(url: string, data?: any) {
    try {
      const result = await axios({
        url: this.getUrl(url),
        method: "delete",
        data,
        headers: this.getHeader(),
      });

      if (result && result.status === 200) {
        return result.data;
      }

      return undefined;
    } catch (error) {
      this.handleErrorStatus(error);
      throw error;
    }
  }
}
export default new ApiManager();
